<script setup lang="ts">
import { toRefs } from 'vue';

import DrawFeatureSelectionView from '#nora-ui/map/components/draw-feature-selection/DrawFeatureSelectionView.vue';
import EditingToolbarView from '#nora-ui/map/components/drawtools/editing/EditingToolbarView.vue';
import RouteToolView from '#nora-ui/map/components/drawtools/route-tool/RouteToolView.vue';
import SymbolToolView from '#nora-ui/map/components/drawtools/symbol-tool/SymbolToolView.vue';
import TacticalAnnotationToolView from '#nora-ui/map/components/drawtools/tool-views/TacticalAnnotationToolView.vue';
import TextToolView from '#nora-ui/map/components/drawtools/tool-views/TextToolView.vue';
import type { BaseDrawTool } from '#nora-ui/map/drawtools/base-draw-tool';
import { RouteTool } from '#nora-ui/map/drawtools/route-tool';
import { SymbolTool } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool';
import { TacticalAnnotationTool } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-tool';
import { TextTool } from '#nora-ui/map/drawtools/text-tool';
import { useMapStore } from '#nora-ui/map/stores/map-store';

export interface DrawToolViewContainerProps {
  isOpen?: boolean;
  mapId: string;
}

const props = withDefaults(defineProps<DrawToolViewContainerProps>(), {
  isOpen: true,
});

const { isOpen, mapId } = toRefs(props);
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(mapId.value);
const drawToolUI = map.drawToolUI;
const activeDrawToolType = drawToolUI.activeDrawToolType;
const activeDrawTool = drawToolUI.activeDrawTool;

function isRouteTool(tool: BaseDrawTool): tool is RouteTool {
  return tool instanceof RouteTool;
}
function isTacticalAnnotationTool(tool: BaseDrawTool): tool is TacticalAnnotationTool {
  return tool instanceof TacticalAnnotationTool;
}
function isTextTool(tool: BaseDrawTool): tool is TextTool {
  return tool instanceof TextTool;
}
function isSymbolTool(tool: BaseDrawTool): tool is SymbolTool {
  return tool instanceof SymbolTool;
}
</script>

<template>
  <div v-if="isOpen">
    <DrawFeatureSelectionView :map-id="mapId" />
    <template v-if="!activeDrawToolType || !activeDrawTool" />
    <RouteToolView
      v-else-if="isRouteTool(activeDrawTool)"
      :tool="activeDrawTool"
      :map-id="mapId"
    />
    <TacticalAnnotationToolView
      v-else-if="isTacticalAnnotationTool(activeDrawTool)"
      :tool="activeDrawTool"
      :map-id="mapId"
    />
    <TextToolView
      v-else-if="isTextTool(activeDrawTool)"
      :tool="activeDrawTool"
      :map-id="mapId"
    />
    <SymbolToolView
      v-else-if="isSymbolTool(activeDrawTool)"
      :tool="activeDrawTool"
      :map-id="mapId"
    />
    <EditingToolbarView
      v-else
      :draw-tool="activeDrawTool"
      :map-id="mapId"
    />
  </div>
</template>
